import { DialogForm } from '@plato/ui-legacy/components/dialog/dialog-form';
import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { useDiscardChangesWarning } from '@plato/ui-legacy/lib/hooks/use-discard-changes-warning';
import { Dialog } from '@plato/ui/containers/dialog';
import { isEqual } from 'lodash-es';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { CrmEventEditScope } from '../../../server/queries/utils/crm-event-build-input.ts';
import type { CrmEventDetails } from '../../../server/types/infer-trpc-types.ts';
import type { RecurrenceRule } from '../../../utils/rrule.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { EventUpdatedSource } from '../../analytics/types/analytics-events.ts';
import { useUpdateEvent } from '../hooks/use-update-event.ts';
import { getInputFromCrmEventDetails } from '../utils/get-input-from-crm-event-details.ts';
import type { EventFormData } from './event-form-fields.tsx';
import { EventFormFields } from './event-form-fields.tsx';
import { RecurrenceScopeDialog } from './recurrence-scope-dialog.tsx';

export type EventEditDialogProps = {
    eventId: string;
    eventDetails: CrmEventDetails;
    occurrenceDate?: string;
    analyticsSource: EventUpdatedSource;
    onUpdated?: () => void;
};

/** Weekday order never counts as a change: the server sorts on write, the form may not. */
function isSameRule(a?: RecurrenceRule, b?: RecurrenceRule): boolean {
    if (!a || !b) {
        return !a && !b;
    }

    const weekdaysOf = (rule: RecurrenceRule) => rule.weekdays.toSorted((x, y) => x - y).join(',');

    return a.weeks === b.weeks && a.until === b.until && weekdaysOf(a) === weekdaysOf(b);
}

export const EventEditDialog = DialogStateManager.create<EventEditDialogProps>(
    ({ eventId, eventDetails, occurrenceDate, analyticsSource, onUpdated }) => {
        const { t } = useTranslation('events');
        const { toast } = useToast();
        const { visible, remove } = useDialog();
        const sendEvent = useAnalytics();

        const originalValues = getInputFromCrmEventDetails(eventDetails);
        const formDefaults = {
            ...originalValues,
            date: occurrenceDate ?? originalValues.date,
        };

        const updateEventMutation = useUpdateEvent({
            onSuccess: (_, { data }) => {
                sendEvent('event_updated', {
                    is_recurring: !!data.recurrenceRule,
                    has_set_time: !!data.start_time && !!data.end_time,
                    source: analyticsSource,
                    has_customer: !!data.customer_id,
                    address_changed: data.address !== originalValues.address,
                    invitees_added: !!data.attendees && data.attendees.length > 0,
                    has_agenda: !!data.agenda,
                });
                remove();
                onUpdated?.();
            },
            onError: (message) => {
                toast({
                    icon: 'warning',
                    title: 'Error',
                    description: message ?? 'An error occurred',
                });
            },
        });

        const form = useForm<EventFormData>({
            defaultValues: formDefaults,
            mode: 'onBlur',
        });

        const {
            handleSubmit,
            formState: { isDirty },
        } = form;

        const { confirmDiscard } = useDiscardChangesWarning(isDirty);

        const handleClose = () => {
            confirmDiscard(remove);
        };

        const submitMutation = (data: EventFormData, scope?: CrmEventEditScope) => {
            updateEventMutation.mutate({
                eventId,
                data,
                occurrenceDate,
                ...(scope ? { scope } : {}),
            });
        };

        const onSubmit = async (data: EventFormData) => {
            const isRecurring = !!eventDetails.recurrenceRule;

            if (!isRecurring) {
                submitMutation(data);
                return;
            }

            const recurrenceChanged = !isSameRule(data.recurrenceRule, originalValues.recurrenceRule);
            const nonRecurrenceChanged =
                data.title !== originalValues.title ||
                data.customer_id !== originalValues.customer_id ||
                data.address !== originalValues.address ||
                data.start_time !== originalValues.start_time ||
                data.end_time !== originalValues.end_time ||
                data.date !== formDefaults.date ||
                !isEqual(data.attendees ?? [], originalValues.attendees ?? []) ||
                !isEqual(data.agenda, originalValues.agenda);

            // If the recurrence pattern has changed, we can skip the confirmation dialog and directly update following occurrences
            if (recurrenceChanged) {
                submitMutation(data, 'following');
                return;
            } else if (!nonRecurrenceChanged) {
                remove();
                return;
            }

            await DialogStateManager.show(RecurrenceScopeDialog, {
                onConfirm: (scope) => {
                    submitMutation(data, scope);
                },
            });
        };

        return (
            <Dialog open={visible} onClose={handleClose} disablePointerDismissal>
                <DialogForm className="sm:max-w-2xl">
                    <DialogForm.Header title={t('events__form_edit_title')} />
                    <DialogForm.Content>
                        <EventFormFields
                            form={form}
                            customer={eventDetails.customer}
                            ownerName={eventDetails.owner_name ?? ''}
                            eventId={eventId}
                        />
                    </DialogForm.Content>
                    <DialogForm.SubmitBlock
                        onCancel={handleClose}
                        onSubmit={handleSubmit(onSubmit)}
                        disabled={updateEventMutation.isPending}
                    />
                </DialogForm>
            </Dialog>
        );
    },
);
